// @/plugins/axios.js
import Axios from 'axios';
import axiosRetryLib from 'axios-retry';

// References that will be set during plugin installation
let notifyOffline = null;
let sentryHandler = null;

/**
 * Redact sensitive information from headers
 */
const redactHeaders = headers => {
    if (!headers || typeof headers !== 'object') return {};

    const sensitiveKeys = [
        'authorization',
        'cookie',
        'set-cookie',
        'x-api-key',
        'x-idempotency-key'
    ];

    const redacted = { ...headers };
    Object.keys(redacted).forEach(key => {
        if (sensitiveKeys.includes(key.toLowerCase())) {
            redacted[key] = '[REDACTED]';
        }
    });

    return redacted;
};

/**
 * Redact sensitive information from request/response data
 */
const redactData = (data, maxSize = 1000) => {
    if (!data) return data;

    // Handle string data
    if (typeof data === 'string') {
        if (data.length > maxSize) {
            return data.substring(0, maxSize) + '... [truncated]';
        }
        return data;
    }

    // Handle non-object data
    if (typeof data !== 'object') return data;

    // Handle arrays
    if (Array.isArray(data)) {
        if (data.length > 10) {
            return [
                ...data.slice(0, 10),
                `... [${data.length - 10} more items]`
            ];
        }
        return data.map(item => redactData(item, maxSize));
    }

    // Handle objects
    const sensitiveKeys = [
        'password',
        'token',
        'authorization',
        'cardNumber',
        'cvv',
        'securityCode',
        'paymentMethodData'
    ];

    const redacted = {};
    for (const [key, value] of Object.entries(data)) {
        if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
            redacted[key] = '[REDACTED]';
        } else if (typeof value === 'string' && value.length > maxSize) {
            redacted[key] = value.substring(0, maxSize) + '... [truncated]';
        } else if (typeof value === 'object' && value !== null) {
            redacted[key] = redactData(value, maxSize);
        } else {
            redacted[key] = value;
        }
    }

    return redacted;
};

/**
 * Extract error message from API response
 */
const extractErrorMessage = data => {
    if (!data) return null;

    if (data.message) return data.message;
    if (data.error)
        return typeof data.error === 'string' ? data.error : data.error.message;
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
        const firstError = data.errors[0];
        return typeof firstError === 'string'
            ? firstError
            : firstError.message || JSON.stringify(firstError);
    }

    return null;
};

const createAxiosInstance = () => {
    return Axios.create({
        baseURL: import.meta.env.VITE_API_URL,
        headers: {
            common: {
                Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        },
        timeout: 30000 // Add a reasonable timeout
    });
};

const axiosRetry = createAxiosInstance();
const axios = createAxiosInstance();
// Add retry functionality
axiosRetryLib(axiosRetry, {
    retries: 8,
    shouldResetTimeout: true,
    retryDelay: retryCount => retryCount * 500,
    retryCondition: error => {
        if (
            axiosRetryLib.isNetworkError(error) ||
            axiosRetryLib.isRetryableError(error)
        ) {
            return true;
        }

        if (
            error.code === 'ECONNABORTED' &&
            error.config?.method?.toLowerCase() === 'get'
        ) {
            return true;
        }

        return false;
    }
});
axiosRetry.interceptors.response.use(
    response => response,
    error => {
        // Handle network errors
        if (error.isAxiosError) {
            if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
                // Show notification if the function is available
                if (notifyOffline) {
                    notifyOffline();
                }
            }
        }

        // Only send to sentry on final retry failure
        const retryConfig = error.config?.['axios-retry'];
        const retryCount = retryConfig?.retryCount ?? 0;
        const maxRetries = retryConfig?.retries ?? 0;

        if (retryCount < maxRetries) {
            console.info(
                `[axios-retry] Retry ${retryCount} of ${maxRetries} for ${error.config?.url}`
            );
        }

        if (retryCount >= maxRetries && sentryHandler) {
            if (!error.response) {
                sentryHandler.handleNetworkError(error);
            } else {
                sentryHandler.handleResponseError(error);
            }
        }

        return Promise.reject(error);
    }
);

// Attach error interceptor to plain axios instance (no retry logic)
axios.interceptors.response.use(
    response => response,
    error => {
        // Handle network errors
        if (error.isAxiosError) {
            if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
                // Show notification if the function is available
                if (notifyOffline) {
                    notifyOffline();
                }
            }
        }

        if (sentryHandler) {
            if (!error.response) {
                sentryHandler.handleNetworkError(error);
            } else {
                sentryHandler.handleResponseError(error);
            }
        }

        return Promise.reject(error);
    }
);

export { axiosRetry, axios };

export default {
    install(Vue, options = {}) {
        const Sentry = options.Sentry;

        // Set up notification function
        notifyOffline = () => {
            Vue.prototype.$notify({
                title: `It seems that you're offline`,
                group: 'offline',
                text: 'Please check your internet connection and try again.',
                duration: 8000
            });
        };

        // Set up Sentry handlers if Sentry is available
        if (Sentry) {
            sentryHandler = {
                handleNetworkError: error => {
                    const isOffline = !navigator.onLine;
                    const isTimeout = error.code === 'ECONNABORTED';
                    const retryCount =
                        error.config?.['axios-retry']?.retryCount ?? 0; // axios‑retry stores this

                    // extra breadcrumb so the network hiccup is visible in related errors too
                    Sentry.addBreadcrumb({
                        category: 'network',
                        message: `${error.config?.method?.toUpperCase() || 'GET'} ${error.config?.url}`,
                        level: 'info',
                        data: { isTimeout, retryCount }
                    });

                    Sentry.withScope(scope => {
                        /* ---------- tags & context ---------- */
                        scope.setTag('error_type', 'network_error');
                        scope.setTag('offline', String(isOffline));
                        scope.setTag('timeout', String(isTimeout));

                        scope.setContext('request', {
                            url: error.config?.url,
                            method: error.config?.method,
                            baseURL: error.config?.baseURL,
                            timeout: error.config?.timeout,
                            retryCount,
                            timestamp: new Date().toISOString()
                        });

                        if (navigator.connection) {
                            const { effectiveType, downlink, rtt } =
                                navigator.connection;
                            scope.setContext('connection', {
                                effectiveType,
                                downlink,
                                rtt
                            });
                        }

                        scope.setLevel(isOffline ? 'info' : 'warning');

                        Sentry.captureException(error);
                    });
                },

                handleResponseError: error => {
                    const { response, config } = error;
                    const {
                        status,
                        data,
                        statusText,
                        headers: responseHeaders
                    } = response || {};

                    const method = (config?.method || 'get').toUpperCase();
                    const url = config?.url || '';
                    const fullUrl = config?.baseURL
                        ? `${config.baseURL}${url}`
                        : url;

                    const errorMessage = extractErrorMessage(data);

                    Sentry.withScope(scope => {
                        scope.setTag('http_status', status);
                        scope.setTag('http_method', method);

                        const requestId =
                            responseHeaders?.['x-request-id'] ||
                            config?.headers?.['x-request-id'];
                        if (requestId) {
                            scope.setTag('request_id', requestId);
                        }

                        scope.setContext('request', {
                            url: fullUrl,
                            method,
                            baseURL: config?.baseURL,
                            path: url,
                            timeout: config?.timeout,
                            params: redactData(config?.params),
                            data: redactData(config?.data),
                            headers: redactHeaders(config?.headers)
                        });

                        scope.setContext('response', {
                            status,
                            statusText,
                            data: redactData(data),
                            headers: redactHeaders(responseHeaders),
                            errorMessage
                        });

                        if (status === 404) {
                            scope.setLevel('info');
                        } else if (status >= 500) {
                            scope.setLevel('error');
                        } else if (status >= 400) {
                            scope.setLevel('warning');
                        }

                        Sentry.captureException(error);
                    });
                }
            };
        }

        // Attach to Vue
        Vue.prototype.$axiosRetry = axiosRetry;
        Vue.prototype.$axios = axios;

        if (options.store) {
            options.store.constructor.prototype.$axiosRetry = axiosRetry;
            options.store.constructor.prototype.$axios = axios;
        }
    }
};
